import { EDITABLE_LINK_BUTTON, TEXT } from '../../items';
import { ItemOfType } from './types';
import { decodeHtmlEntities } from '~/lib/decodeHtmlEntities';
import { templateTextFieldsEnabled } from '../../utils/templateTextFields';

type Card = ItemOfType<'Card'>;

/** What learners get where a card's button has no label or URL of its own. */
const DEFAULT_BUTTON_TITLE = 'More';
const DEFAULT_BUTTON_URL = 'https://www.easyllama.com/';

/** A card's link button is its first sub-item. */
const buttonOf = (card: Card) =>
  card.items?.[0] as
    | {
        id?: string;
        title?: string | null;
        subtitle?: string | null;
        url?: string | null;
      }
    | undefined;

/** Whether the card has a button of its own, which edits can be saved to. */
export const hasButton = (card: Card) => Boolean(buttonOf(card)?.id);

export const getButtonId = (card: Card) =>
  buttonOf(card)?.id || `${card.id}_button`;

export const getButtonTitle = (card: Card) =>
  buttonOf(card)?.title || DEFAULT_BUTTON_TITLE;

// GraphQL returns 'url', REST (editor) returns 'subtitle' for the button URL.
// Either is stored as HTML (a bare "&" as "&amp;"), so it is decoded once.
const savedButtonUrl = (card: Card) => {
  const button = buttonOf(card);
  return decodeHtmlEntities(button?.url || button?.subtitle || '');
};

export const getButtonUrl = (card: Card) =>
  savedButtonUrl(card) || DEFAULT_BUTTON_URL;

/**
 * The editor's link button: its label is editable text, its URL editable in
 * place. `learnerDefaults` says what learners get for an empty label or URL:
 * the default label and page (true), or no button at all (false). The editor
 * shows each the way learners would get it.
 */
export const EditableLinkButtonHelper = (
  card: Card,
  { learnerDefaults }: { learnerDefaults: boolean }
) => ({
  type: EDITABLE_LINK_BUTTON as typeof EDITABLE_LINK_BUTTON,
  id: getButtonId(card),
  values: {
    url: learnerDefaults ? getButtonUrl(card) : savedButtonUrl(card)
  },
  items: [
    {
      type: TEXT as typeof TEXT,
      id: `${getButtonId(card)}_title`,
      values: {
        // Emptied, a label saves what learners get in its place, or is
        // cleared behind its cue: both for a company with the template text
        // fields only. Otherwise it saves as before.
        ...(learnerDefaults
          ? {
              value: getButtonTitle(card),
              ...(templateTextFieldsEnabled()
                ? { emptyText: DEFAULT_BUTTON_TITLE }
                : {})
            }
          : {
              value: buttonOf(card)?.title || '',
              ...(templateTextFieldsEnabled() ? { emptyHint: 'Add label' } : {})
            }),
        variant: 'text',
        allowNewLines: false,
        textAlignment: false,
        disableRewrite: true
      },
      items: [] as never[]
    }
  ]
});
